import * as THREE from "three";

export type Spherical3 = {
  /** Distance from target to position. */
  radius: number;
  /** Azimuth around Y-axis (radians), atan2(dx, dz). */
  azimuth: number;
  /** Polar angle from +Y (radians), acos(dy/r) ∈ [0, π]. */
  polar: number;
};

const TWO_PI = Math.PI * 2;

/**
 * Convert `(target, position)` to spherical coordinates around `target`.
 *
 * Convention matches `SightManager.getCameraSetupForSight` so backend-supplied
 * `sight.cameraConfig.{azimuth, polar}` round-trip through this:
 *   x = r·sin(polar)·cos(azimuth)
 *   y = r·cos(polar)
 *   z = r·sin(polar)·sin(azimuth)
 *
 * Inverse: `azimuth = atan2(z, x)`, `polar = acos(y / r)`. azimuth=0 →
 * camera in +X relative to target; azimuth=π/2 → +Z; azimuth=π → -X;
 * azimuth=-π/2 → -Z. Round-trip is exact for non-degenerate radii.
 *
 * Degenerate (`radius < 1e-6`) returns `radius=0` and `azimuth=0`; callers
 * should treat near-zero radius as "polar interpolation undefined" and
 * fall back to a linear path.
 */
export function cartesianToSpherical(
  target: THREE.Vector3,
  position: THREE.Vector3,
): Spherical3 {
  const dx = position.x - target.x;
  const dy = position.y - target.y;
  const dz = position.z - target.z;
  const radius = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (radius < 1e-6) {
    return { radius: 0, azimuth: 0, polar: 0 };
  }
  const azimuth = Math.atan2(dz, dx);
  const polar = Math.acos(THREE.MathUtils.clamp(dy / radius, -1, 1));
  return { radius, azimuth, polar };
}

/**
 * Convert `(target, spherical)` back to a 3D position. Inverse of
 * {@link cartesianToSpherical} for non-degenerate radii. `out` is optional
 * scratch storage; a fresh `Vector3` is allocated when omitted.
 */
export function sphericalToCartesian(
  target: THREE.Vector3,
  sph: Spherical3,
  out?: THREE.Vector3,
): THREE.Vector3 {
  const sinPolar = Math.sin(sph.polar);
  const x = sph.radius * sinPolar * Math.cos(sph.azimuth);
  const y = sph.radius * Math.cos(sph.polar);
  const z = sph.radius * sinPolar * Math.sin(sph.azimuth);
  const dst = out ?? new THREE.Vector3();
  return dst.set(target.x + x, target.y + y, target.z + z);
}

/**
 * Shortest signed angular distance from `from` to `to` in radians,
 * normalised to `[-π, π]`. Used so azimuth lerp picks the short arc
 * (e.g. 350° → 10° goes the +20° way, not the −340° way).
 */
export function shortestAngleDelta(from: number, to: number): number {
  let delta = (to - from) % TWO_PI;
  if (delta > Math.PI) delta -= TWO_PI;
  else if (delta < -Math.PI) delta += TWO_PI;
  return delta;
}
